'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { CircleCheck, ChevronRight } from 'lucide-react';
import NumberFlow from '@number-flow/react';
import { apiFetch } from '@/lib/api';
import { recordCompletenessScore } from '@/lib/completeness';
import DashboardMetricCard from '../DashboardMetricCard';

type MissingItem = { field: string; label: string; weight: number; url: string };
type CompletenessResponse = { score: number; missing: MissingItem[] };

function ProgressRing({ score }: { score: number }) {
  const radius = 28;
  const stroke = 5;
  const norm = radius - stroke / 2;
  const circumference = 2 * Math.PI * norm;
  const safe = Math.max(0, Math.min(100, score));
  const offset = circumference - (safe / 100) * circumference;

  return (
    <svg width={radius * 2} height={radius * 2} className="-rotate-90 flex-shrink-0">
      <defs>
        <linearGradient id="completeness-ribbon" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#F03060" />
          <stop offset="100%" stopColor="#F0785A" />
        </linearGradient>
      </defs>
      <circle
        cx={radius}
        cy={radius}
        r={norm}
        stroke="currentColor"
        strokeWidth={stroke}
        className="text-muted/40"
        fill="transparent"
      />
      <circle
        cx={radius}
        cy={radius}
        r={norm}
        stroke="url(#completeness-ribbon)"
        strokeWidth={stroke}
        className="transition-[stroke-dashoffset] duration-700 ease-out"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
        fill="transparent"
      />
    </svg>
  );
}

export default function ProfileCompletenessCard() {
  const [data, setData] = useState<CompletenessResponse | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch('/api/proxy/api/profile/completeness', undefined, { method: 'GET' });
        if (!res.ok) {
          if (!cancelled) setError(true);
          return;
        }
        const json = (await res.json()) as CompletenessResponse;
        if (cancelled) return;
        setData(json);
        recordCompletenessScore(json.score);
      } catch {
        if (!cancelled) setError(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const icon = <CircleCheck className="h-4 w-4" strokeWidth={1.75} />;

  if (error) {
    return (
      <DashboardMetricCard title="Profile completeness" icon={icon}>
        <div className="text-sm text-muted-foreground">Couldn&apos;t load completeness right now.</div>
      </DashboardMetricCard>
    );
  }

  if (data === null) {
    return (
      <DashboardMetricCard title="Profile completeness" icon={icon}>
        <div className="space-y-3">
          <div className="h-9 w-16 rounded bg-muted animate-pulse" />
          <div className="h-3 w-36 rounded bg-muted/60 animate-pulse" />
        </div>
      </DashboardMetricCard>
    );
  }

  if (data.score === 100) {
    return (
      <DashboardMetricCard title="Profile completeness" icon={icon}>
        <div className="flex items-center gap-3">
          <ProgressRing score={100} />
          <div>
            <div className="font-serif text-2xl font-semibold tabular-nums">100%</div>
            <div className="text-xs text-muted-foreground">Discoverable in search.</div>
          </div>
        </div>
      </DashboardMetricCard>
    );
  }

  const top = data.missing.slice(0, 3);

  return (
    <DashboardMetricCard title="Profile completeness" icon={icon}>
      <div className="flex items-center gap-3 mb-3">
        <ProgressRing score={data.score} />
        <div>
          <div className="font-serif text-2xl font-semibold tabular-nums">
            <NumberFlow value={data.score} />%
          </div>
          <div className="text-xs text-muted-foreground">
            {data.missing.length} item{data.missing.length === 1 ? '' : 's'} left
          </div>
        </div>
      </div>
      {top.length > 0 ? (
        <ul className="space-y-1">
          {top.map((m) => (
            <li key={m.field}>
              <Link
                href={m.url}
                className="flex items-center justify-between gap-2 rounded-lg px-2 py-1.5 -mx-2 text-sm hover:bg-primary/5 transition-colors"
              >
                <span className="truncate">{m.label}</span>
                <span className="flex items-center gap-0.5 text-xs font-medium text-primary flex-shrink-0">
                  +{m.weight}%
                  <ChevronRight className="h-3.5 w-3.5" strokeWidth={1.75} />
                </span>
              </Link>
            </li>
          ))}
        </ul>
      ) : null}
    </DashboardMetricCard>
  );
}
